<script setup lang="ts">
import { cx } from 'class-variance-authority'

import ReviewsSmall from '~/components/workshop/reviews-small.vue'
import { useWorkshop } from '~/composables/cms/workshop'

import CheckGreen from '~/assets/icons/check-green.svg?component'

import Menu from '~/components/workshop/menu.vue'

import WorkshopDescription from '~/components/workshop/workshop-description.vue'
import CardsSwiper from '~/components/workshop/cards-swiper.vue'
import Prices from '~/components/workshop/prices.vue'
import OurProjects from '~/components/workshop/our-projects.vue'
import WorkshopTeam from '~/components/workshop/worskhop-team.vue'
import BlogSection from '~/components/workshop/blog-section.vue'

import SingleTestimonial from '~/components/managed-projects/single-testimonial.vue'
import OtherServices from '~/components/workshop/other-services.vue'

const {
  public: { baseUrl },
} = useRuntimeConfig()

const fetch = useWorkshop()
const lp = useLocalePath()
const { defineOgMeta } = useSeoAttributes()
const pageMeta = usePageMeta()

const [{ data: meta }, { data: workshop }] = await Promise.all([
  useAsyncData('workshop-meta', async () => pageMeta.fetchByPath('/product-discovery-workshop')),
  useAsyncData('workshop-page', async () => fetch.fetch()),
])

const { clients } = useHome()

const pixelGridLg = [
  '  111111111111111111111111111111111111111111111   ',
  ' 1111111111111111     111111211111111111111111    ',
  '  112221111111122111111111221211111111111111111   ',
  '       211111111111111111111111111111    2111111  ',
  '       211111111111111111111111111111    2111111  ',
  '                                    1    2111111  ',
  '       2111111                     11    2111111  ',
  '1111                     111111122111 221111111   ',
  '1111111111                  111111122111 221111111',
  '1111111111                                        ',
  '                            111111122111 221111111',
  '                            111111122111 221111111',
  '                                  122111 221111111',
  '                                      11 221111111',
].join('\n')

if (workshop.value && workshop.value.page_meta) {
  useHead(() => ({
    title: workshop.value?.page_meta?.title,
    link: [{ rel: 'canonical', href: `${baseUrl}/product-discovery-workshop` }],
    meta: defineOgMeta(workshop.value?.page_meta ?? null),
  }))
}

const currentTab = ref<string>('Startup')

const scrollToElement = async (elementId: string) => {
  await nextTick()
  const element = document.querySelector(`#${elementId}`)?.scrollIntoView({ behavior: 'smooth' })
}

function onTabChange(option: string) {
  currentTab.value = option
}

const gradient = 'text-transparent bg-clip-text text-transparent leading-tight bg-gradient-to-b'
</script>

<template>
  <Container
    v-if="workshop"
    class="screen-content flex flex-col items-center space-y-6 pb-16 pt-32 md:pb-16 lg:pb-16 lg:pt-48"
  >
    <div class="mt-16 flex w-full flex-1 items-center justify-between">
      <div>
        <ReviewsSmall class="text-center lg:text-left" />

        <Heading size="display" as="h1" class="xs:text-center xs:mb-8 w-full lg:mb-4 lg:text-left">
          <span :class="cx(gradient, 'from-gray-950 to-gray-600')">{{ $t('workshop.title.top') }}</span
          ><br />
          <span class="pr-1" :class="cx(gradient, 'from-main-700 to-main-500')">{{ $t('workshop.title.bottom') }}</span>
        </Heading>

        <Paragraph class="xs:px-12 xs:text-center mb-8 text-gray-600 lg:w-[85%] lg:px-0 lg:text-left" size="medium">
          {{ workshop.hero?.body }}
        </Paragraph>

        <div class="flex flex-col sm:justify-center lg:justify-start">
          <div class="xs:space-y-4 flex flex-col space-x-0 md:flex-row md:space-x-4 lg:space-y-0">
            <Action
              class="lg:text-md flex w-full items-center justify-center py-3 text-sm font-normal md:w-28 lg:w-44"
              :intent="workshop.hero?.action?.intent"
            >
              <NuxtLink :href="workshop.hero?.action?.href" target="_blank">{{
                workshop.hero?.action?.label
              }}</NuxtLink>
            </Action>

            <Paragraph size="small" class="xs:text-center visible py-4 pt-2 text-gray-600 md:hidden lg:text-left">
              Just <span class="font-bold text-gray-800">{{ workshop.hero?.spots + ' spots' + ' ' }}</span
              >left this month
            </Paragraph>

            <Action
              class="lg:text-md flex w-full items-center justify-center py-3 text-sm font-normal md:w-28 lg:w-32"
              intent="normal"
            >
              <NuxtLink @click.prevent="scrollToElement('prices')" class="cursor-pointer">
                {{ $t('workshop.hero_button') }}
              </NuxtLink>
            </Action>
          </div>

          <Paragraph size="small" class="xs:text-center invisible py-8 pt-4 text-gray-600 md:visible lg:text-left">
            Just <span class="font-bold text-gray-800">{{ workshop.hero?.spots + ' spots' + ' ' }}</span
            >left this month
          </Paragraph>
        </div>
      </div>

      <Menu class="hidden md:block" @update-tab="onTabChange" :title="workshop.card?.title">
        <li
          v-for="option in currentTab === 'Startup' ? workshop.card?.startup_options : workshop.card?.options"
          class="text-md mb-4 flex list-none items-center gap-2 space-y-2 rounded-md border border-gray-300 p-3 text-gray-600"
        >
          <CheckGreen class="h-6 w-6" />
          {{ option.title }}
        </li>
      </Menu>
    </div>

    <Partners :title="false" :partners="workshop.testimonials!.partners" class="mt-4 px-8 pb-0 lg:px-0" />

    <template #outer>
      <PixelGrid class="absolute top-0 w-full" :pattern="pixelGridLg" />
    </template>
  </Container>

  <Container class="mx-auto flex flex-col items-center">
    <WorkshopDescription />
  </Container>

  <CardsSwiper />

  <div id="prices">
    <Prices />
  </div>

  <OurProjects :data="workshop?.projects!" />

  <WorkshopTeam />

  <SingleTestimonial class="border-b-0 py-14 lg:py-24" :data="workshop?.succession" />

  <Container v-if="workshop" class="screen-content flex flex-col items-center px-2">
    <Testimonials class="w-full" :testimonials="workshop.testimonials?.testimonials || []" />
  </Container>

  <Container
    v-if="workshop"
    class="screen-content flex flex-col items-center pb-16 pt-32 text-center md:pb-16 lg:pb-16 lg:pt-48"
  >
    <Heading size="caption" as="h2">{{ workshop.faq?.headline }}</Heading>
    <Heading as="h3" size="large" class="w-full lg:w-1/2">{{ workshop.faq?.title }}</Heading>
    <Paragraph class="mt-2 w-full pb-6 lg:w-1/2">{{ workshop.faq?.body }}</Paragraph>

    <Accordion
      v-if="workshop.faq?.questions"
      class="xs:px-12 mt-8 pb-16 text-sm xl:px-72"
      :items="workshop.faq?.questions"
    />
  </Container>

  <BlogSection :data="workshop?.blog || undefined" />

  <OtherServices :data="workshop?.other_services" />

  <LazyCta class="pt-24" :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
</template>
